import React from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    MenuItem,
    Paper,
    Snackbar,
    Stack,
    TextField,
    Typography,
    alpha,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {
    Refresh as RefreshIcon,
    Checklist as ChecklistIcon,
} from "@mui/icons-material";
import { EASYFLEX_STATUS_FILTER_OPTIONS, EasyflexFreshness, EasyflexStatusFilter } from "../api/workersApi";
import EasyFlexWorkersTable from "../components/Worker/EasyFlexWorkersTable";
import { useEasyflexWorkers } from "../hooks/useEasyflexWorkers";

/**
 * Dedicated EasyFlex worker page — the Central-DB (read-only) view, split out of
 * the workers/resources table. Browse EasyFlex workers, filter by status,
 * trigger sync jobs, and import (single or bulk) into recruitment.
 */
const EasyFlexPage: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const ef = useEasyflexWorkers();

    return (
        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, mb: 1.5, border: `1px solid ${theme.palette.divider}` }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                    <Box>
                        <Typography variant="h6" fontWeight={800}>EasyFlex Workers</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Central DB view (read-only) • import workers into recruitment
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip
                            size="small"
                            label={`${ef.totalCount.toLocaleString()} result${ef.totalCount === 1 ? "" : "s"}`}
                            sx={{
                                backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.2 : 0.08),
                                color: theme.palette.text.primary,
                                fontWeight: 600,
                            }}
                        />
                        <Button
                            size="small"
                            variant={ef.bulkSelectMode ? "contained" : "outlined"}
                            startIcon={<ChecklistIcon />}
                            onClick={ef.toggleBulkSelectMode}
                        >
                            {ef.bulkSelectMode ? "Exit bulk select" : "Bulk select"}
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={ef.refresh}
                            disabled={ef.refreshing}
                        >
                            {ef.refreshing ? "Refreshing…" : "Refresh"}
                        </Button>
                    </Stack>
                </Stack>

                {/* EasyFlex-native toolbar: freshness / status / sync scope / orgs */}
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.5}
                    sx={{ mt: 1.5 }}
                    alignItems={{ xs: "stretch", md: "center" }}
                >
                    <TextField
                        select
                        label="Refresh mode"
                        value={ef.freshness}
                        onChange={(e) => ef.setFreshness(e.target.value as EasyflexFreshness)}
                        size={isMobile ? "small" : "medium"}
                        sx={{ minWidth: { xs: "100%", md: 180 } }}
                        helperText={ef.freshness === "cached" ? "Fetch cached Central DB view" : "Trigger Central DB sync job on refresh"}
                    >
                        <MenuItem value="cached">Cached</MenuItem>
                        <MenuItem value="refresh_async">Refresh async</MenuItem>
                        <MenuItem value="refresh_sync">Refresh sync (wait)</MenuItem>
                    </TextField>

                    <TextField
                        select
                        label="Status"
                        value={ef.statusFilter}
                        onChange={(e) => ef.setStatusFilter(String(e.target.value || "") as "" | EasyflexStatusFilter)}
                        size={isMobile ? "small" : "medium"}
                        sx={{ minWidth: { xs: "100%", md: 200 } }}
                        helperText="Filter by status"
                    >
                        <MenuItem value="">All</MenuItem>
                        {EASYFLEX_STATUS_FILTER_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select
                        label="Sync scope"
                        value={ef.syncKey}
                        onChange={(e) => ef.setSyncKey(String(e.target.value || "").trim())}
                        size={isMobile ? "small" : "medium"}
                        sx={{ minWidth: { xs: "100%", md: 160 } }}
                        helperText="Which job to trigger"
                    >
                        <MenuItem value="workers">Workers</MenuItem>
                        <MenuItem value="placements">Placements</MenuItem>
                        <MenuItem value="hours">Hours</MenuItem>
                        <MenuItem value="all">All</MenuItem>
                    </TextField>

                    <TextField
                        label="Organizations (optional)"
                        placeholder="optimaUitzendburo,optimaWork"
                        value={ef.organizationsInput}
                        onChange={(e) => ef.setOrganizationsInput(e.target.value)}
                        size={isMobile ? "small" : "medium"}
                        helperText="Blank = all orgs; comma-separated override"
                        sx={{ flex: 1, minWidth: { xs: "100%", md: 280 } }}
                    />
                </Stack>

                {(ef.lastSyncRunId || ef.lastSyncState || ef.lastSyncError) && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                        {ef.lastSyncRunId && (
                            <Chip size="small" variant="outlined" label={`syncRunId: ${ef.lastSyncRunId}`} sx={{ fontFamily: "monospace", maxWidth: { xs: "100%", md: 360 } }} />
                        )}
                        {ef.lastSyncState && (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`job: ${ef.lastSyncState}`}
                                color={ef.lastSyncState === "failed" ? "error" : ef.lastSyncState === "completed" ? "success" : "default"}
                            />
                        )}
                        {ef.lastSyncError && (
                            <Chip size="small" variant="outlined" color="warning" label={ef.lastSyncError} sx={{ maxWidth: { xs: "100%", md: 380 } }} />
                        )}
                    </Stack>
                )}
            </Paper>

            {ef.bulkSelectMode && ef.selectedCount > 0 && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 1.25,
                        mb: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.5 : 0.35)}`,
                        backgroundColor: alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.2 : 0.08),
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                    }}
                >
                    <Typography variant="body2" fontWeight={700}>
                        {ef.selectedCount} EasyFlex worker{ef.selectedCount === 1 ? "" : "s"} selected
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button variant="outlined" size="small" onClick={ef.clearSelection} disabled={ef.bulkImporting}>
                            Clear
                        </Button>
                        <Button variant="contained" size="small" color="success" onClick={ef.bulkImport} disabled={ef.bulkImporting}>
                            {ef.bulkImporting ? "Importing…" : `Import ${ef.selectedCount} to recruitment`}
                        </Button>
                    </Stack>
                </Paper>
            )}

            <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: "hidden" }}>
                <EasyFlexWorkersTable
                    workers={ef.workers}
                    loading={ef.loading}
                    currentPage={ef.page}
                    rowsPerPage={ef.rowsPerPage}
                    totalCount={ef.totalCount}
                    onPageChange={ef.setPage}
                    onRowsPerPageChange={ef.setRowsPerPage}
                    sortField={ef.sortField}
                    sortDir={ef.sortOrder}
                    onSortChange={ef.setSort}
                    searchValue={ef.search}
                    onSearchChange={ef.setSearch}
                    onImport={ef.openImportWizard}
                    linkedMap={ef.linkedMap}
                    onOpenWorker={ef.openWorker}
                    bulkSelectMode={ef.bulkSelectMode}
                    selectedIds={ef.selectedIds}
                    onToggleRow={ef.toggleRow}
                    onTogglePage={ef.togglePage}
                    onSelectAllMatching={ef.selectAllMatching}
                    isSelectingAllMatching={ef.isSelectingAllMatching}
                    allMatchingSelected={ef.allMatchingSelected}
                />
            </Paper>

            <Snackbar
                open={ef.snackbar.open}
                autoHideDuration={6000}
                onClose={ef.dismissSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={ef.dismissSnackbar} severity={ef.snackbar.severity} variant="filled" sx={{ width: "100%" }}>
                    {ef.snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default EasyFlexPage;
